import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DayPicker } from 'react-day-picker';

import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/button';

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
	iconSize?: string;
};

const Calendar = ({
	className,
	classNames,
	showOutsideDays = true,
	iconSize = 'h-4 w-4',
	...props
}: CalendarProps) => {
	return (
		<DayPicker
			showOutsideDays={showOutsideDays}
			className={cn('p-4', className)}
			classNames={{
				months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
				month: 'space-y-4',
				caption: 'flex justify-center pt-1 relative items-center',
				caption_label: 'text-sm font-medium',
				nav: 'space-x-1 flex items-center',
				nav_button: cn(
					buttonVariants({ variant: 'outline' }),
					'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100'
				),
				nav_button_previous: 'absolute left-1',
				nav_button_next: 'absolute right-1',
				table: 'w-full border-collapse space-y-1',
				head_row: 'flex !max-w-[272px] mx-auto',
				head_cell:
					'text-slate-500 rounded-md w-9 font-normal text-[0.8rem] dark:text-slate-400',
				row: 'flex w-full mt-2 !max-w-[272px] mx-auto',
				cell: 'h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-slate-100/50 [&:has([aria-selected])]:bg-slate-100 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20 dark:[&:has([aria-selected].day-outside)]:bg-slate-800/50 dark:[&:has([aria-selected])]:bg-slate-800 ',
				day: cn(
					buttonVariants({ variant: 'ghost' }),
					'h-9 w-9 p-0 font-normal aria-selected:opacity-100 '
				),
				day_range_end: 'day-range-end ',
				day_selected:
					'bg-purple-600 text-white hover:bg-purple-700 hover:text-white focus:bg-purple-700 focus:text-white',
				day_today: ` ${
					props.selected
						? 'text-black border-none'
						: 'bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-slate-50'
				}`,
				day_outside:
					'day-outside text-slate-500 aria-selected:bg-slate-100/50 aria-selected:text-slate-500 dark:text-slate-400 dark:aria-selected:bg-slate-800/50 dark:aria-selected:text-slate-400 ',
				day_disabled: 'text-slate-500 opacity-50 dark:text-slate-400 ',
				day_range_middle:
					'aria-selected:bg-slate-100 !text-slate-900 aria-selected:text-slate-900 dark:aria-selected:bg-slate-800 dark:aria-selected:text-slate-50 ',
				day_hidden: 'invisible ',
				...classNames,
			}}
			components={{
				IconLeft: () => <ChevronLeft className={iconSize} />,
				IconRight: () => <ChevronRight className={iconSize} />,
			}}
			{...props}
		/>
	);
};
Calendar.displayName = 'Calendar';

export { Calendar };
